import { useSurveyLogging } from "./useSurveyLogging";
import { Box, type BoxRef, Button, Heading, Pressable, Text, TextArea } from "../../../../mads";
import { useI18n, useLocale } from "../../../../shared/I18n";
import { useIntersectionObserverItem } from "../../../../shared/util/intersectionObserver/useIntersectionObserverItem";

import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import { useCallback, useState, useRef, useEffect } from "react";
import { StyleSheet } from "react-native";

/**
 * Wrapper that captures touch events to prevent them from bubbling
 * up to the parent JobCard pressable.
 */
const TouchCaptureWrapper = ({ children }: { children: React.ReactNode }) => (
  <Pressable onPress={() => {}} accessible={false} sx={styles.touchCaptureWrapper}>
    {children}
  </Pressable>
);

/** Debounce duration (ms) for the onRatingChange callback */
export const RATING_CHANGE_DEBOUNCE_MS = 2000;

export type SurveyState = "initial" | "rating-selected" | "completed";

export interface RelevanceSurveyProps {
  jobKey: string;
  trackingKey?: string;
  position?: number;
}

/**
 * Relevance survey component that renders below job card attributes.
 * Allows users to rate job recommendations on a 1-5 scale and optionally
 * provide text feedback.
 *
 * Matches the web implementation design with:
 * - 1-5 rating buttons with "Very poor", "Just OK", "Very good" labels
 * - Optional textarea for feedback after rating selection
 * - Submit button and completed state with Edit option
 *
 * Survey events are logged to OneGraph:
 * - Impression: on mount (survey rendered)
 * - Seen: when survey scrolls into viewport (first time only)
 * - Submitted: on rating selection (after debounce) or button press
 */
export const RelevanceSurvey = ({ jobKey, trackingKey, position }: RelevanceSurveyProps) => {
  const i18n = useI18n();
  const { language } = useLocale();
  const [surveyState, setSurveyState] = useState<SurveyState>("initial");
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>("");
  const ratingDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasSubmittedRef = useRef(false);

  // Survey logging via OneGraph mutations
  const { recordImpression, recordSeen, recordSubmitted } = useSurveyLogging({
    jobKey,
    trackingKey,
    position,
  });

  // Fire impression event on mount
  useEffect(() => {
    recordImpression();
  }, [recordImpression]);

  // Visibility detection for "seen" event
  const handleVisibilityChange = useCallback(
    ({ isIntersecting }: { isIntersecting: boolean }) => {
      if (isIntersecting) {
        recordSeen();
      }
    },
    [recordSeen],
  );

  const { ref: visibilityRef } = useIntersectionObserverItem({
    onChange: handleVisibilityChange,
    triggerOnce: true,
    threshold: { size: 50 },
  });

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (ratingDebounceRef.current) {
        clearTimeout(ratingDebounceRef.current);
      }
    };
  }, []);

  const handleRatingSelect = useCallback(
    (selectedRating: number) => {
      // Update local state immediately for responsive UI
      setRating(selectedRating);
      setSurveyState("rating-selected");

      // Debounce the submission to prevent rapid-fire calls
      // when users quickly change their selection (e.g., arrowing through options)
      if (ratingDebounceRef.current) {
        clearTimeout(ratingDebounceRef.current);
      }
      ratingDebounceRef.current = setTimeout(() => {
        // Auto-submit rating only (without feedback text) after debounce
        // This fires the "submitted" event but doesn't transition to completed state,
        // allowing the user to add feedback and submit again with the full data
        if (!hasSubmittedRef.current) {
          hasSubmittedRef.current = true;
          recordSubmitted(selectedRating, undefined);
        }
      }, RATING_CHANGE_DEBOUNCE_MS);
    },
    [recordSubmitted],
  );

  const handleFeedbackChange = useCallback((text: string) => {
    setFeedback(text);
  }, []);

  const handleSubmit = useCallback(() => {
    if (rating !== null) {
      // Cancel any pending debounced rating callback
      if (ratingDebounceRef.current) {
        clearTimeout(ratingDebounceRef.current);
      }

      // Submit the survey with rating and optional feedback
      const feedbackValue = feedback.trim() || undefined;
      recordSubmitted(rating, feedbackValue);
      hasSubmittedRef.current = true;
      setSurveyState("completed");
    }
  }, [rating, feedback, recordSubmitted]);

  const handleEdit = useCallback(() => {
    setSurveyState("rating-selected");
    // Reset submission flag so auto-submit can fire again if user changes rating
    hasSubmittedRef.current = false;
  }, []);

  // Plain English rating adjective for a given rating value. Used ONLY to bold the
  // adjective in the English source render (see the follow-up-question render). English
  // is the source language, so this needs no gettext lookup; the fully-formed,
  // translatable sentences live in getQuestionString.
  const getEnglishRatingAdjective = (): string => {
    switch (rating) {
      case 1:
        return "very poor";
      case 2:
        return "poor";
      case 3:
        return "just OK";
      case 4:
        return "good";
      case 5:
        return "very good";
      default:
        return "";
    }
  };

  // Fully-formed, independently translatable follow-up question per rating (RN-6334 /
  // JOBCARDS-7119). Each rating has its own complete sentence so translators can produce
  // grammatically correct copy without a mid-sentence variable. msgctxt/msgid match web
  // MR !3720 so catalogs stay consistent across web and native.
  const getQuestionString = (): string => {
    switch (rating) {
      case 1:
        return i18n.pgettext(
          'relevance survey follow-up question, shown after a "very poor" (1 of 5) rating',
          "What makes it a very poor recommendation?",
        );
      case 2:
        return i18n.pgettext(
          'relevance survey follow-up question, shown after a "poor" (2 of 5) rating',
          "What makes it a poor recommendation?",
        );
      case 3:
        return i18n.pgettext(
          'relevance survey follow-up question, shown after a "just OK" (3 of 5) rating',
          "What makes it a just OK recommendation?",
        );
      case 4:
        return i18n.pgettext(
          'relevance survey follow-up question, shown after a "good" (4 of 5) rating',
          "What makes it a good recommendation?",
        );
      case 5:
        return i18n.pgettext(
          'relevance survey follow-up question, shown after a "very good" (5 of 5) rating',
          "What makes it a very good recommendation?",
        );
      default:
        return "";
    }
  };

  const getRatingDescription = (value: number): string => {
    switch (value) {
      case 1:
        return i18n.gettext("Very poor");
      case 2:
        return i18n.gettext("Poor");
      case 3:
        return i18n.gettext("Just OK");
      case 4:
        return i18n.gettext("Good");
      case 5:
        return i18n.gettext("Very good");
      default:
        return "";
    }
  };

  // Completed state
  if (surveyState === "completed") {
    return (
      <TouchCaptureWrapper>
        <Box
          ref={visibilityRef as React.RefObject<BoxRef>}
          sx={styles.container}
          testID="relevance-survey-completed"
        >
          <Box sx={styles.completedContainer}>
            <Text level={2} sx={styles.completedText}>
              {i18n.gettext("Thanks for your feedback!")}
            </Text>
            <Button variant="text" size="sm" onPress={handleEdit}>
              {i18n.gettext("Edit")}
            </Button>
          </Box>
        </Box>
      </TouchCaptureWrapper>
    );
  }

  // Initial or rating-selected state
  return (
    <TouchCaptureWrapper>
      <Box
        ref={visibilityRef as React.RefObject<BoxRef>}
        sx={styles.container}
        testID="relevance-survey"
        accessibilityLabel={i18n.gettext("Job relevance survey")}
      >
        {/* Header */}
        <Heading level={3} nativeID={`relevance-survey-header-${jobKey}`} sx={styles.title}>
          {i18n.gettext("Rate this job recommendation")}
        </Heading>
        <Text level={2} sx={styles.subtitle}>
          {i18n.gettext("Help us improve Indeed")}
        </Text>

        {/* Rating buttons */}
        <Box
          sx={styles.ratingContainer}
          accessibilityRole="radiogroup"
          accessibilityLabelledBy={`relevance-survey-header-${jobKey}`}
        >
          <Box sx={styles.ratingButtonsRow}>
            {[1, 2, 3, 4, 5].map((value, index) => (
              <RatingButton
                key={value}
                value={value}
                selected={rating === value}
                onPress={() => handleRatingSelect(value)}
                isFirst={index === 0}
                isLast={index === 4}
                ratingDescription={getRatingDescription(value)}
              />
            ))}
          </Box>
          <Box sx={styles.ratingLabelsRow}>
            <Text level={1} sx={styles.ratingLabelLeft}>
              {i18n.gettext("Very poor")}
            </Text>
            <Text level={1} sx={styles.ratingLabelCenter}>
              {i18n.gettext("Just OK")}
            </Text>
            <Text level={1} sx={styles.ratingLabelRight}>
              {i18n.gettext("Very good")}
            </Text>
          </Box>
        </Box>

        {/* Feedback textarea (shown after rating selection) */}
        {surveyState === "rating-selected" && rating !== null && (
          <Box sx={styles.feedbackContainer}>
            <Text level={2} sx={styles.feedbackQuestion}>
              {language !== "en" ? (
                // Non-English: render the fully-formed, independently-translated sentence
                // as plain text. Each rating has its own msgid — there is no mid-sentence
                // variable to inject, and the adjective's position inside a translated
                // sentence is unknown, so it is not bolded.
                getQuestionString()
              ) : (
                // English (source copy): render the known sentence with the rating
                // adjective in bold. The surrounding words need no lookup; the sentence is
                // still extracted for translators via the pgettext call in getQuestionString.
                <>
                  {"What makes it a "}
                  <Text level={2} sx={styles.feedbackRatingLabel}>
                    {getEnglishRatingAdjective()}
                  </Text>
                  {" recommendation?"}
                </>
              )}
            </Text>
            <Box sx={styles.textAreaWrapper}>
              <TextArea
                value={feedback}
                onInput={handleFeedbackChange}
                ariaText={i18n.gettext("Feedback about this job recommendation")}
                textAreaHeight={80}
              />
            </Box>
          </Box>
        )}

        {/* Submit button */}
        {surveyState === "rating-selected" && (
          <Box sx={styles.submitContainer}>
            <Button variant="primary" size="sm" onPress={handleSubmit}>
              {i18n.gettext("Submit")}
            </Button>
          </Box>
        )}
      </Box>
    </TouchCaptureWrapper>
  );
};

/**
 * Individual rating button (1-5).
 * Defined outside parent component per RULES.md React best practices.
 */
const RatingButton = ({
  value,
  selected,
  onPress,
  isFirst,
  isLast,
  ratingDescription,
}: {
  value: number;
  selected: boolean;
  onPress: () => void;
  isFirst: boolean;
  isLast: boolean;
  ratingDescription: string;
}) => {
  return (
    <Box sx={styles.ratingButtonContainer}>
      <Pressable
        onPress={onPress}
        accessibilityRole="radio"
        accessibilityState={{ selected }}
        accessibilityLabel={`${value}, ${ratingDescription}`}
        sx={[
          styles.ratingButton,
          selected ? styles.ratingButtonSelected : styles.ratingButtonUnselected,
          isFirst && styles.ratingButtonFirst,
          isLast && styles.ratingButtonLast,
        ]}
      >
        <Text
          level={2}
          sx={selected ? styles.ratingButtonTextSelected : styles.ratingButtonTextUnselected}
        >
          {String(value)}
        </Text>
      </Pressable>
      {!isLast && <Box sx={styles.ratingButtonDivider} />}
    </Box>
  );
};

const styles = StyleSheet.create({
  touchCaptureWrapper: {
    marginTop: spacing.s4,
    marginHorizontal: -spacing.s4,
    marginBottom: -spacing.s4,
  },
  container: {
    paddingTop: spacing.s4,
    paddingBottom: spacing.s4,
    paddingHorizontal: spacing.s4,
    backgroundColor: colors.expressiveBlue100,
    borderTopWidth: 1,
    borderTopColor: colors.borderWeak,
  },
  title: {
    marginBottom: spacing.s1,
  },
  subtitle: {
    color: colors.foreground,
    marginBottom: spacing.s3,
  },
  ratingContainer: {
    marginBottom: spacing.s2,
  },
  ratingButtonsRow: {
    flexDirection: "row",
    borderRadius: borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.borderWeak,
    overflow: "hidden",
  },
  ratingButtonContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  ratingButton: {
    flex: 1,
    paddingVertical: spacing.s3,
    alignItems: "center",
    justifyContent: "center",
  },
  ratingButtonFirst: {
    borderTopLeftRadius: borderRadius.sm,
    borderBottomLeftRadius: borderRadius.sm,
  },
  ratingButtonLast: {
    borderTopRightRadius: borderRadius.sm,
    borderBottomRightRadius: borderRadius.sm,
  },
  ratingButtonDivider: {
    width: 1,
    height: "50%",
    backgroundColor: colors.borderWeak,
  },
  ratingButtonSelected: {
    backgroundColor: colors.foregroundStrong,
    borderRadius: borderRadius.sm,
  },
  ratingButtonUnselected: {
    backgroundColor: colors.background,
  },
  ratingButtonTextSelected: {
    color: colors.foregroundInverse,
    fontWeight: "700",
  },
  ratingButtonTextUnselected: {
    color: colors.foregroundStrong,
    fontWeight: "700",
  },
  ratingLabelsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: spacing.s1,
  },
  ratingLabelLeft: {
    color: colors.foregroundStrong,
    flex: 1,
    textAlign: "left",
  },
  ratingLabelCenter: {
    color: colors.foregroundStrong,
    flex: 1,
    textAlign: "center",
  },
  ratingLabelRight: {
    color: colors.foregroundStrong,
    flex: 1,
    textAlign: "right",
  },
  feedbackContainer: {
    marginTop: spacing.s4,
    marginBottom: spacing.s4,
  },
  feedbackQuestion: {
    marginBottom: spacing.s2,
    color: colors.foregroundStrong,
  },
  feedbackRatingLabel: {
    fontWeight: "700",
    color: colors.foregroundStrong,
  },
  textAreaWrapper: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
  },
  submitContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  completedContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  completedText: {
    fontWeight: "700",
    color: colors.foregroundStrong,
  },
});
